/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'
import qs from 'query-string'
import { isEmpty, isEqual } from 'lodash'

import {
  Heading,
  SvgIcon,
  Spinner,
  SpanWithData,
  Warning,
  TabbedView,
  ToolbarItem,
  CreateCaseModal,
  ToolbarObjectCases,
  PayrollRunSummaryView,
  PayrollRunClientsView,
  PayrollRunPaymentsView,
  PayrollRunCasesView
} from '../components'

import {
  decodeBase64,
  hasResponsibility
} from '../utils'

import {
  CONTENTTYPE_PAYROLLRUN
} from '../constants'

const createCaseModalRef = React.createRef()
const casesCountAndCreateRef = React.createRef()
const caseListingRef = React.createRef()

class PayrollRun extends React.Component {
  static propTypes = {
    id: PropTypes.string
  }

  state = {
    tabView: 'summary',
    showFilter: '',
    refetch: false,
    userHasUpdatePermission: null,
    userIsPicsaEmployee: JSON.parse(localStorage.getItem('isUserPicsaEmployee')),
    showCreateCaseModal: false
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.data.error && !this.state.refetch) {
      this.setState({ refetch: true })
      nextProps.data.refetch()
    }

    if (this.state.refetch) {
      this.setState({ refetch: false })
    }

    if (nextProps.data.payrollrun && this.state.userHasUpdatePermission === null) {
      if (this.state.userIsPicsaEmployee) {
        this.setState({
          userHasUpdatePermission: hasResponsibility('self', 'deductions_editor', 'payrollcycle')
        })
      } else {
        this.setState({ userHasUpdatePermission: false })
      }
    }
  }

  UNSAFE_componentWillMount() {
    this.initFiltersFromQueryString()
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.location.search !== this.props.location.search) {
      this.initFiltersFromQueryString()
    }
  }

  handleClickOutside = (e) => {
    if (createCaseModalRef.current && !createCaseModalRef.current.contains(e.target)) {
      // click is outside, close modal
      this.setState({ showCreateCaseModal: false })
    }
  }

  initFiltersFromQueryString = () => {
    let queryParams = qs.parse(this.props.location.search)

    if (isEmpty(queryParams)) {
      this.setQueryString()
    } else {
      let filters = Object.assign(
        {},
        ...Object.keys(queryParams).map(key => (  // ES6 way of assigning renamed object keys
          ({
            [key]: queryParams[key],
          })
        ))
      )

      if (!isEqual(queryParams, this.state.filters)) {
        this.setState(filters)
      }
    }
  }

  setQueryString = () => {
    let filters = {
      orderBy: this.state.orderBy,
      tabView: this.state.tabView
    }

    this.props.history.push({ search: qs.stringify(filters) })
  }

  showCreateCaseModal = (identityId) => (
    <CreateCaseModal
      forwardedRef={createCaseModalRef}
      followUp={true}
      affects={identityId}
      caseRelations={[
        {
          objectId: decodeBase64(this.props.id || this.props.match.params.payrollRunId),
          contentType: CONTENTTYPE_PAYROLLRUN
        }
      ]}
      onCloseClick={() => this.setState({ showCreateCaseModal: false })}
      onSaveClick={() => this.setState({ showCreateCaseModal: false })}
      onCaseRelationsCreated={() => {
        casesCountAndCreateRef.current.props.updateData()  // updates CasesCountAndCreate open/resolved cases count
        caseListingRef.current.props.updateData()  // updates the cases view's table to correspond with cases count
      }}
    />
  )

  getSelectedTabIndex = () => {
    switch (this.state.tabView) {
      case 'summary':
        return 0
      case 'clients':
        return 1
      case 'payments':
        return 2
      case 'cases':
        return 3
      default:
        return 0
    }
  }

  handleTabClick = (tab) => {
    switch (tab) {
      case 0:
        this.setState({ tabView: 'summary' }, this.setQueryString)
        break
      case 1:
        this.setState({ tabView: 'clients' }, this.setQueryString)
        break
      case 2:
        this.setState({ tabView: 'payments' }, this.setQueryString)
        break
      case 3:
        this.setState({ tabView: 'cases' }, this.setQueryString)
        break
      default:
        break
    }
  }

  refetchData = () => this.props.data.refetch()

  render() {
    if (this.props.data.loading) {
      return <Spinner type='brand' size='large' top={true} className='viewport-height-100' />
    }

    if (this.props.data.error) {
      return (
        <Warning className='slds-m-around_large u-font-size-larger'>
          We&rsquo;re experiencing a problem loading your data
        </Warning>
      )
    }

    let payrollRun = this.props.data.payrollrun
    let payrollCycle = payrollRun.payrollcycle

    if (this.state.userIsPicsaEmployee &&
      (hasResponsibility('self', 'deductions_reviewer', 'payrollcycle', payrollCycle.id) ||
      this.state.userHasUpdatePermission)) {

      let workflowRunDeducting = payrollRun.workflowrunDeducting
      let batchPayment = payrollRun.batchPayment
      let uploadDisabled = !(this.state.userIsPicsaEmployee || this.state.userHasUpdatePermission) ||
        (workflowRunDeducting && workflowRunDeducting.atStep > 8)
      let paymentExpandAndEditDisallowed = payrollRun.workflowrunDeducting === null ||
        !this.state.userHasUpdatePermission ||
        payrollRun.workflowrunDeducting.atStep > 8 ||
        payrollRun.workflowrunDeducting.status === 'PROCESSED'

      return (
        <div className='slds-size_1-of-1'>
          <PayrollRunHeader
            payrollRun={payrollRun.id}
            startsOn={payrollRun.startsOn}
            endsOn={payrollRun.endsOn}
            paidOn={payrollRun.paidOn}
            payrollCycleEmployer={payrollCycle.employer.friendlyName}
            payrollCycleName={payrollCycle.name}
            status={payrollRun.status}
            internalApprover={payrollRun.internalApprover}
            internalApprovalAt={payrollRun.internalApprovalAt}
            amountApprover={payrollRun.amountApprover}
            amountApprovalAt={payrollRun.amountApprovalAt}
            paymentApprover={payrollRun.paymentApprover}
            paymentApprovalAt={payrollRun.paymentApprovalAt}
            onCaseCreateClick={() => this.setState({ showCreateCaseModal: true })}
          />
          <TabbedView
            tabs={[ 'Summary', 'Clients', 'Payments', 'Cases' ]}
            contents={[
              <PayrollRunSummaryView
                payrollRunId={payrollRun.id}
                workflowRunDeducting={workflowRunDeducting}
                workflowRunReceipting={payrollRun.workflowrunReceipting}
                paymentsSummary={JSON.parse(payrollRun.paymentSummary)['payments']}
                paymentsTotal={batchPayment
                  ? batchPayment.parentTotalAmount
                  : null
                }
                uploadDisabled={uploadDisabled}
                refetchData={this.refetchData}
              />,
              <PayrollRunClientsView
                payrollCycleId={payrollRun.payrollcycle.id}
                workflowRunDeductingId={workflowRunDeducting
                  ? workflowRunDeducting.id
                  : null
                }
                parentPaymentId={batchPayment
                  ? batchPayment.id
                  : null
                }
                clientsSummary={JSON.parse(payrollRun.paymentSummary)['clients']}
                uploadDisabled={uploadDisabled}
                editDisabled={paymentExpandAndEditDisallowed}
                onRefreshClick={this.refetchData}
              />,
              <PayrollRunPaymentsView
                payrollRunId={payrollRun.id}
                payrollCycleId={payrollRun.payrollcycle.id}
                workflowRunDeductingId={workflowRunDeducting
                  ? workflowRunDeducting.id
                  : null
                }
                paymentsSummary={JSON.parse(payrollRun.paymentSummary)['payments']}
                paymentsTotal={batchPayment
                  ? batchPayment.parentTotalAmount
                  : 0
                }
                removedPayments={JSON.parse(payrollRun.paymentsBreakdown)["removed"]}
                uploadDisabled={uploadDisabled}
                paymentExpandDisabled={paymentExpandAndEditDisallowed}
                employerFriendlyName={payrollRun.payrollcycle.employer.friendlyName}
                refetchData={this.refetchData}
              />,
              <PayrollRunCasesView
                forwardedRef={caseListingRef}
                payrollRun={payrollRun.id}
              />
            ]}
            selectedTab={this.getSelectedTabIndex()}
            onClick={this.handleTabClick}
            tabsClassName='slds-align_absolute-center slds-border_top'
            backgroundColour='#fff'
            loadContentOnTabClick={true}
            disabledTabs={workflowRunDeducting === null || batchPayment === null
              ? [1, 2]
              : null
            }
          />
          {this.state.showCreateCaseModal && this.showCreateCaseModal(payrollRun.payrollcycle.employer.identity.id)}
        </div>
      )
    } else {
      return (
        <div className='slds-p-around_xx-large slds-grid'>
          <SvgIcon
            variant='utility'
            symbol='lock'
            className='slds-m-right_medium'
          />
          <Heading className='slds-m-top_x-small'>
            Unfortunately, you don&rsquo;t have permission to view deduction details.
          </Heading>
        </div>
      )
    }
  }
}

const PayrollRunHeader = ({ payrollRun, startsOn, endsOn, paidOn, payrollCycleEmployer, payrollCycleName, status,
  internalApprover, internalApprovalAt, amountApprover, amountApprovalAt, paymentApprover, paymentApprovalAt, onCaseCreateClick }) => (
  <div id='payrollRunHeader' className='slds-grid slds-wrap deduction-header'>
    <div className='slds-col slds-p-top_large slds-p-left_medium slds-p-bottom_small slds-p-right_medium'>
      <div className='slds-text-title_caps'>
        {payrollCycleEmployer}
      </div>
      <div className='slds-text-heading_medium u-no-margin__b'>
        <b>
          {`${payrollCycleName} - ${moment(paidOn).format('Do MMMM YYYY')}`}
        </b>
      </div>
      <div className='slds-text-title_caps'>
        {`Period: ${moment(startsOn).format('Do MMMM YYYY')} to ${moment(endsOn).format('Do MMMM YYYY')}`}
      </div>
    </div>
    <div className='slds-col u-flex-align-self side-panel'>
      <PayrollRunStatus
        status={status}
        internalApproverName={`${internalApprover ? `${internalApprover.firstName} ${internalApprover.lastName}` : 'Unknown'}`}
        internalApproved={internalApprovalAt !== null}
        amountApproverName={`${amountApprover ? `${amountApprover.firstName} ${amountApprover.lastName}` : 'Unknown'}`}
        amountApproved={amountApprovalAt !== null}
        paymentApproverName={`${paymentApprover ? `${paymentApprover.firstName} ${paymentApprover.lastName}` : 'Unknown'}`}
        paymentApproved={paymentApprovalAt !== null}
      />
    </div>
    <div>
      <ToolbarObjectCases
        ref={casesCountAndCreateRef}
        objectId={payrollRun}
        elementId='payrollRunHeader'
        onCaseCreateClick={onCaseCreateClick}
      />
    </div>
  </div>
)

const PayrollRunStatus = ({ status, internalApproverName, internalApproved, amountApproverName, amountApproved, paymentApproverName, paymentApproved }) => {
  return (
    <div className='slds-border_left' style={{ lineHeight: '1em' }}>
      <ToolbarItem
        label='Status'
        value={<SpanWithData forField='PayrollRunStatus' searchKey={status} />}
      />
      {/* next step description */}
      <ToolbarItem
        label='Next action'
        value={paymentApproved
          ? 'None'
          : amountApproved
            ? `Approve payment ${paymentApproverName}`
            : internalApproved
              ? `Approve amount ${amountApproverName}`
              : `Approve deduction ${internalApproverName}`
        }
      />
    </div>
  )
}

const getPayrollRun = gql`
  query ($id: ID!) {
    payrollrun(id: $id) {
      id
      startsOn
      endsOn
      paidOn
      paymentsBreakdown
      status
      paymentSummary
      batchPayment {
        id
        parentTotalAmount
      }
      payrollcycle {
        id
        name
        employer {
          id
          friendlyName
          identity {
            id
          }
        }
      }
      workflowrunDeducting {
        id
        atStep
        status
        workflowrunsteps {
          edges {
            node {
              id
              workflowstep {
                id
                number
              }
            }
          }
        }
      }
      workflowrunReceipting {
        id
      }
      internalApprover {
        id
        firstName
        lastName
      }
      internalApprovalAt
      amountApprover {
        id
        firstName
        lastName
      }
      amountApprovalAt
      paymentApprover {
        id
        firstName
        lastName
      }
      paymentApprovalAt
    }
  }
`

const withData =
  graphql(getPayrollRun, {
    options: ({ id, match }) => ({
      variables: {
        id: id || match.params.payrollRunId
      },
      fetchPolicy: 'network-only'
    })
  })

export default withData(PayrollRun)



// WEBPACK FOOTER //
// ./src/containers/PayrollRun.js